import { useEffect, useRef } from "react";
import { useAdContext } from "./AdProvider";
import type { AdFormat } from "./types";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    adsbygoogle?: any[];
  }
}

export type AdSlotProps = {
  /** 広告ユニットID */
  slotId: string;
  /** 広告フォーマット */
  format?: AdFormat;
  /** レスポンシブ対応 */
  responsive?: boolean;
  /** カスタムクラス */
  className?: string;
  /** 最小高さ（広告読み込み中のレイアウトシフト防止） */
  minHeight?: number;
};

/**
 * AdSense広告スロット
 * 個別の広告ユニットを表示
 */
export function AdSlot({
  slotId,
  format = "auto",
  responsive = true,
  className = "",
  minHeight = 100,
}: AdSlotProps) {
  const adContext = useAdContext();
  const adRef = useRef<HTMLModElement>(null);
  const isAdPushed = useRef(false);

  useEffect(() => {
    // 広告が無効または未読み込みの場合
    if (!adContext?.config.enabled || !adContext.isLoaded) {
      return;
    }

    // 既にpush済みの場合はスキップ
    if (isAdPushed.current) {
      return;
    }

    // 少し遅延させてDOMが安定してから広告をロード
    const timer = setTimeout(() => {
      try {
        if (window.adsbygoogle && adRef.current) {
          window.adsbygoogle.push({});
          isAdPushed.current = true;
        }
      } catch (error) {
        console.warn("[Ad] Failed to push ad:", error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [adContext?.config.enabled, adContext?.isLoaded]);

  // 広告が無効の場合は何も表示しない
  if (!adContext?.config.enabled) {
    return null;
  }

  // テストモードの場合はプレースホルダーを表示
  if (adContext.config.testMode) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/50 text-sm text-muted-foreground ${className}`}
        style={{ minHeight }}
      >
        <span>広告プレースホルダー ({slotId})</span>
      </div>
    );
  }

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle block ${className}`}
      style={{ display: "block", minHeight }}
      data-ad-client={adContext.config.publisherId}
      data-ad-slot={slotId}
      data-ad-format={format}
      data-full-width-responsive={responsive ? "true" : "false"}
    />
  );
}
